/**
 * Full-page view for essay comments.
 *
 * Called by:
 * - App.tsx as route for /reviewer/essays/:slug/comments
 */
import { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Button, Space, Spin, Tag } from 'antd';
import { ArrowLeftOutlined, CommentOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEssay, getComments } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import CommentPanel from '../components/CommentPanel';

const { Title, Text } = Typography;

export default function EssayCommentsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: essay, isLoading: essayLoading } = useQuery({
    queryKey: ['essay', slug],
    queryFn: () => getEssay(slug!),
    enabled: !!slug,
  });

  const { data: comments = [], isLoading: commentsLoading } = useQuery({
    queryKey: ['comments', slug],
    queryFn: () => getComments(slug!),
    enabled: !!slug && !!user,
  });

  const handleCommentsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['comments', slug] });
  }, [queryClient, slug]);

  const unresolvedCount = comments.filter((c) => !c.resolved).length;

  if (essayLoading || commentsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link to={`/reviewer/essays/${slug}`}>
          <Button type="text" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Back to Essay
          </Button>
        </Link>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <CommentOutlined style={{ fontSize: 20, color: '#d4af37' }} />
            <Title level={3} style={{ margin: 0 }}>Comments</Title>
            {unresolvedCount > 0 && (
              <Tag color="gold">{unresolvedCount} unresolved</Tag>
            )}
          </Space>
        </Space>
        {essay && (
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {essay.title}
          </Text>
        )}
      </div>

      {/* Comments panel */}
      <CommentPanel
        essaySlug={slug!}
        comments={comments}
        currentUser={user}
        onCommentsChange={handleCommentsChange}
      />
    </div>
  );
}
